import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';

const RoadmapEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prestataires, setPrestataires] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [formData, setFormData] = useState({
    event_title: '',
    event_date: '',
    blocks: [],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [roadmapRes, prestRes, rolesRes] = await Promise.all([
        axios.get(`${API}/roadmaps/${id}`),
        axios.get(`${API}/prestataires`, { withCredentials: true }),
        axios.get(`${API}/prestataire-roles`)
      ]);
      
      setFormData({
        event_title: roadmapRes.data.roadmap.event_title,
        event_date: roadmapRes.data.roadmap.event_date,
        blocks: roadmapRes.data.roadmap.blocks || [],
        notes: roadmapRes.data.roadmap.notes || ''
      });
      setPrestataires(prestRes.data);
      setRoleOptions(rolesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/roadmaps/${id}`, formData, { withCredentials: true });
      toast.success('Feuille de route mise à jour');
      navigate('/backoffice/roadmaps');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = () => {
    setFormData({
      ...formData,
      blocks: [
        ...formData.blocks,
        {
          title: '',
          date: '',
          heure: '',
          lieu: '',
          adresse: '',
          prestataires: []
        }
      ]
    });
  };

  const removeBlock = (index) => {
    setFormData({
      ...formData,
      blocks: formData.blocks.filter((_, i) => i !== index)
    });
  };

  const updateBlock = (index, field, value) => {
    const newBlocks = [...formData.blocks];
    newBlocks[index] = { ...newBlocks[index], [field]: value };
    setFormData({ ...formData, blocks: newBlocks });
  };

  const addPrestataireToBlock = (blockIndex) => {
    const newBlocks = [...formData.blocks];
    newBlocks[blockIndex].prestataires.push({ role: '', name: '', prestataire_id: '' });
    setFormData({ ...formData, blocks: newBlocks });
  };

  const removePrestataireFromBlock = (blockIndex, prestIndex) => {
    const newBlocks = [...formData.blocks];
    newBlocks[blockIndex].prestataires = newBlocks[blockIndex].prestataires.filter((_, i) => i !== prestIndex);
    setFormData({ ...formData, blocks: newBlocks });
  };

  const updatePrestataire = (blockIndex, prestIndex, field, value) => {
    const newBlocks = [...formData.blocks];
    newBlocks[blockIndex].prestataires[prestIndex] = {
      ...newBlocks[blockIndex].prestataires[prestIndex],
      [field]: value
    };

    // Auto-fill name if prestataire_id is selected
    if (field === 'prestataire_id' && value) {
      const presta = prestataires.find(p => p.id === value);
      if (presta) {
        newBlocks[blockIndex].prestataires[prestIndex].name = `${presta.prenom} ${presta.nom}`;
      }
    }

    setFormData({ ...formData, blocks: newBlocks });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/backoffice/roadmaps')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <h1 className="text-2xl font-bold text-stone-800">Modifier la feuille de route</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* En-tête */}
        <Card>
          <CardHeader>
            <CardTitle>Informations générales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Titre de l'événement</Label>
              <Input
                value={formData.event_title}
                onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Date(s)</Label>
              <Input
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Blocs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-stone-800">Événements</h2>
            <Button type="button" onClick={addBlock} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un événement
            </Button>
          </div>

          {formData.blocks.map((block, blockIndex) => (
            <Card key={blockIndex}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Événement {blockIndex + 1}</CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBlock(blockIndex)}
                >
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type d'événement</Label>
                    <Input
                      value={block.title}
                      onChange={(e) => updateBlock(blockIndex, 'title', e.target.value)}
                      placeholder="Ex: Synagogue, Réception"
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={block.date}
                      onChange={(e) => updateBlock(blockIndex, 'date', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Heure</Label>
                    <Input
                      type="time"
                      value={block.heure}
                      onChange={(e) => updateBlock(blockIndex, 'heure', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Lieu</Label>
                    <Input
                      value={block.lieu}
                      onChange={(e) => updateBlock(blockIndex, 'lieu', e.target.value)}
                      placeholder="Nom du lieu"
                    />
                  </div>
                </div>

                <div>
                  <Label>Adresse complète</Label>
                  <Input
                    value={block.adresse}
                    onChange={(e) => updateBlock(blockIndex, 'adresse', e.target.value)}
                    placeholder="Adresse complète"
                  />
                </div>

                {/* Prestataires */}
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">Équipe assignée</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addPrestataireToBlock(blockIndex)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>

                  {block.prestataires.map((presta, prestIndex) => (
                    <div key={prestIndex} className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-4">
                        <Select
                          value={presta.role}
                          onValueChange={(val) => updatePrestataire(blockIndex, prestIndex, 'role', val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Select
                          value={presta.prestataire_id}
                          onValueChange={(val) => updatePrestataire(blockIndex, prestIndex, 'prestataire_id', val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Prestataire" />
                          </SelectTrigger>
                          <SelectContent>
                            {prestataires.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.prenom} {p.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={presta.name}
                          onChange={(e) => updatePrestataire(blockIndex, prestIndex, 'name', e.target.value)}
                          placeholder="Nom"
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePrestataireFromBlock(blockIndex, prestIndex)}
                          className="h-9 w-9 p-0"
                        >
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate('/backoffice/roadmaps')}>
            Annuler
          </Button>
          <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RoadmapEditor;
